import { useEffect, useState } from 'react'
import { Tooltip, IconButton, Stack } from '@mui/material'
import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { AlertPhone, Device, Project, RuuviTag, User, UserAlertRule } from '../types'
import { AlertGroup, AlertRule, AlertTemplate, AlertVariables } from '../types/Alerts'
import { useSelector } from 'react-redux'
import { RootState } from '../redux'
import useAuth from '../hooks/useAuth'
import useDataGrid from '../hooks/useDataGrid'
import { usePolicy } from '../contexts/PolicyContext'
import { makeAlertRuleDataGridRow, translateAlertRuleName } from '../utils/alert'
import { AlertRuleName } from '../enums'
import { Edit, Error, Warning } from '@mui/icons-material'
import UserAlertRuleEmailNotificationSwitch from './UserAlertRuleEmailNotificationSwitch'
import UserAlertRuleAppNotificationSwitch from './UserAlertRuleAppNotificationSwitch'
import UserAlertRuleSmsNotificationsSwitch from './UserAlertRuleSmsNotificationsSwitch'
import UserAlertRuleEnableSwitch from './UserAlertRuleEnableSwitch'
import AlertRuleEditButton from './AlertRuleEditButton'
import AlertRuleRemoveButton from './AlertRuleRemoveButton'
import AlertRuleLabel from './AlertRuleLabel'
import { AlertRuleDataGridRow } from '../types/AlertRuleDataGridRow'

const EMPTY_USER_ALERT_RULES: UserAlertRule[] = []

interface AlertRuleDataGridProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  userAlertRules?: UserAlertRule[]
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  user?: User
  alertPhone?: AlertPhone
  showEnabled?: boolean
  useRedux?: boolean
  afterUpdate?: (alertRules: AlertGroup[]) => void
  afterUserAlertRulesUpdate?: (userAlertRules: UserAlertRule[]) => void
}

export default function AlertRuleDataGrid({
  alertRules,
  alertTemplates,
  userAlertRules = EMPTY_USER_ALERT_RULES,
  project,
  device,
  ruuviTag,
  user: initialUser,
  showEnabled = true,
  useRedux = true,
  afterUpdate,
  afterUserAlertRulesUpdate,
  ...props
}: AlertRuleDataGridProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The user.
   */
  const user = initialUser || currentUser

  /**
   * The policy state.
   */
  const {
    canEditProjectAlertRules,
    canEditDeviceAlertRules,
    canEditRuuviTagAlertRules,
    canEditUserAlertRules
  } = usePolicy()

  /**
   * Indicates if the current user can edit alert rules.
   */
  const canEditAlertRules =
    (project && canEditProjectAlertRules(project)) ||
    (device && canEditDeviceAlertRules(device)) ||
    (ruuviTag && canEditRuuviTagAlertRules(ruuviTag))

  const reduxAlertRules = useSelector((state: RootState) => state.users.userAlertRules)

  /**
   * The alert notifications.
   */
  const alertNotifications = useRedux ? reduxAlertRules : userAlertRules

  /**
   * The data grid props.
   */
  const dataGridProps = useDataGrid(props)

  /**
   * The enabled alerts as "rows".
   */
  const [rows, setRows] = useState<AlertRuleDataGridRow[]>(() => getInitialRows())

  /**
   * The columns.
   */
  const columns: GridColDef<AlertRuleDataGridRow>[] = makeColumns()

  function makeColumns(): GridColDef<AlertRuleDataGridRow>[] {
    const columns: GridColDef<AlertRuleDataGridRow>[] = []

    columns.push({
      field: 'name',
      type: 'string',
      headerName: t('name'),
      flex: 1,
      width: 200,
      editable: false,
      valueGetter: (_, row) => translateAlertRuleName(row.name),
      renderCell: ({ row }) => {
        return <AlertRuleLabel alertRule={row} />
      }
    })

    columns.push({
      field: 'alertRule.emailNotificationEnabled',
      headerName: t('emailNotifications'),
      flex: 0,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => row?.userAlertRule?.emailNotificationEnabled ?? false,
      renderCell: ({ row }) => {
        return (
          <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            {user && canEditUserAlertRules(user) && (
              <>
                {row?.userAlertRule === undefined ||
                (device &&
                  row.userAlertRule.deviceName !== device.name &&
                  !row.userAlertRule.emailNotificationEnabled) ? (
                  <UserAlertRuleEnableSwitch
                    type="email"
                    size="small"
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    ruleGroupName={row.group}
                    alertRuleName={row.name}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    afterEnable={handleAfterUserAlertRulesUpdate}
                  />
                ) : (
                  <UserAlertRuleEmailNotificationSwitch
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    userAlertRule={row.userAlertRule}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    disabled={
                      device &&
                      row?.userAlertRule?.deviceName === null &&
                      row.userAlertRule.emailNotificationEnabled
                    }
                    afterSubmit={handleAfterUserAlertRulesUpdate}
                  />
                )}
              </>
            )}
          </Stack>
        )
      }
    })

    columns.push({
      field: 'alertRule.smsNotificationEnabled',
      headerName: t('smsNotifications'),
      flex: 0,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => row?.userAlertRule?.smsNotificationEnabled ?? false,
      renderCell: ({ row }) => {
        return (
          <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
            {user && canEditUserAlertRules(user) && (
              <>
                {row?.userAlertRule === undefined ||
                (device &&
                  row.userAlertRule.deviceName !== device.name &&
                  !row.userAlertRule.smsNotificationEnabled) ? (
                  <UserAlertRuleEnableSwitch
                    type="sms"
                    size="small"
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    ruleGroupName={row.group}
                    alertRuleName={row.name}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    afterEnable={handleAfterUserAlertRulesUpdate}
                  />
                ) : (
                  <UserAlertRuleSmsNotificationsSwitch
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    userAlertRule={row.userAlertRule}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    disabled={
                      device &&
                      row?.userAlertRule?.deviceName === null &&
                      row.userAlertRule.smsNotificationEnabled
                    }
                    afterSubmit={handleAfterUserAlertRulesUpdate}
                  />
                )}
              </>
            )}
          </Stack>
        )
      }
    })

    columns.push({
      field: 'alertRule.appNotificationEnabled',
      headerName: t('appNotifications'),
      flex: 0,
      width: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => row?.userAlertRule?.appNotificationEnabled ?? false,
      renderCell: ({ row }) => {
        return (
          <Stack justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
            {user && canEditUserAlertRules(user) && (
              <>
                {row?.userAlertRule === undefined ||
                (device &&
                  row.userAlertRule.deviceName !== device.name &&
                  !row.userAlertRule.smsNotificationEnabled) ? (
                  <UserAlertRuleEnableSwitch
                    type="app"
                    size="small"
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    ruleGroupName={row.group}
                    alertRuleName={row.name}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    afterEnable={handleAfterUserAlertRulesUpdate}
                  />
                ) : (
                  <UserAlertRuleAppNotificationSwitch
                    user={user}
                    project={project}
                    device={device}
                    ruuviTag={ruuviTag}
                    userAlertRule={row.userAlertRule}
                    userAlertRules={userAlertRules}
                    useRedux={useRedux}
                    disabled={
                      device &&
                      row?.userAlertRule?.deviceName === null &&
                      row.userAlertRule.appNotificationEnabled
                    }
                    afterSubmit={handleAfterUserAlertRulesUpdate}
                  />
                )}
              </>
            )}
          </Stack>
        )
      }
    })

    columns.push({
      field: 'actions',
      type: 'actions',
      headerName: t('actions'),
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <>
          {canEditAlertRules && (
            <>
              {Object.keys(row.variables).some(
                (key) => row.variables[key as keyof AlertVariables]?.readonly
              ) ? (
                <Tooltip title={t('valueCannotBeChanged')}>
                  <IconButton size="small">
                    <Edit color="disabled" fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <AlertRuleEditButton
                  project={project}
                  device={device}
                  ruuviTag={ruuviTag}
                  alertRules={alertRules}
                  alertTemplates={alertTemplates}
                  disabled={!row.enabled}
                  rows={rows}
                  row={row}
                  useRedux={useRedux}
                  afterUpdate={handleAfterUpdate}
                />
              )}
            </>
          )}

          {isAlertRuleEnabledByDefault(row.name) ? (
            <Tooltip title={t('mandatoryAlert')}>
              <IconButton color="warning" size="small">
                <Error fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : !row.enabled ? (
            <Tooltip title={t('disabled')}>
              <IconButton color="warning" size="small">
                <Warning fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <AlertRuleRemoveButton
              project={project}
              device={device}
              ruuviTag={ruuviTag}
              alertRules={alertRules}
              alertTemplates={alertTemplates}
              rule={row.id}
              useRedux={useRedux}
              afterUpdate={handleAfterUpdate}
            />
          )}
        </>
      )
    })

    return columns
  }

  /**
   * Find the alert notification.
   */
  function findAlertNotification(alertRuleName: AlertRuleName) {
    for (const group of alertRules) {
      for (const alert of group.alerts) {
        if (alert.name === alertRuleName) {
          if (device || project) {
            let userAlertRule

            // Try to find the alert rule from device rules.
            if (device) {
              userAlertRule = alertNotifications
                .filter((rule) => rule.deviceName === device.name)
                .find((rule) => rule.alertRuleName === alertRuleName)
            }

            // Fallback to project alert rules when provided.
            if (!userAlertRule && project) {
              userAlertRule = alertNotifications
                .filter((rule) => rule.projectId === project.id)
                .find((rule) => rule.alertRuleName === alertRuleName)
            }

            return userAlertRule
          } else if (ruuviTag) {
            return alertNotifications
              .filter((rule) => rule.ruuviTagId === ruuviTag.id)
              .find((rule) => rule.alertRuleName === alertRuleName)
          }
        }
      }
    }
  }

  /**
   * Get the intial rows from enabled alert rules.
   */
  function getInitialRows(): AlertRuleDataGridRow[] {
    return alertRules
      .map((group: AlertGroup) =>
        group.alerts
          .filter((rule) => (showEnabled ? rule.enabled : true))
          .map((rule: AlertRule) => {
            const alertNotification = findAlertNotification(rule.name)
            return makeAlertRuleDataGridRow(group, rule, alertNotification)
          })
      )
      .flat()
  }

  /**
   * Check if the alert rules is enabled by default.
   */
  function isAlertRuleEnabledByDefault(rule: string): boolean {
    return alertTemplates.some((template) => {
      return Object.keys(template.defaults).some(
        (name) => name === rule && template.defaults[name].enabled
      )
    })
  }

  /**
   * Handle the event after update.
   */
  function handleAfterUpdate(alertRules: AlertGroup[]) {
    afterUpdate?.(alertRules)
  }

  /**
   * Handle the event after user alert rules update.
   */
  function handleAfterUserAlertRulesUpdate(userAlertRules: UserAlertRule[]) {
    afterUserAlertRulesUpdate?.(userAlertRules)
  }

  /**
   * Set rows when project alert rules change.
   */
  useEffect(() => {
    setRows(getInitialRows())
  }, [alertRules, alertNotifications])

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      editMode="row"
      disableColumnMenu
      disableRowSelectionOnClick={!canEditAlertRules}
      getRowHeight={() => 72}
      {...dataGridProps}
    />
  )
}
